import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Typography,
  Box,
  CircularProgress
} from '@mui/material';
import { api } from '../services/api';
import { useToast } from "../components/ToastContext";
import PropTypes from 'prop-types';

const SaveTemplateDialog = ({
  open,
  onClose,
  formData,
  additionalTags,
  resourceId,
  resourceName,
  selectedProvider
}) => {
  const [templateName, setTemplateName] = useState('');
  const [templateError, setTemplateError] = useState('');
  const [loading, setLoading] = useState(false);
  const toast = useToast();

  // Helper function to transform formData to match backend format
  const transformFormDataToResourceRequest = (formData) => {
    const resourceRequest = {};

    Object.keys(formData).forEach(key => {
      if (key === 'applicationId' || key === 'ApplicationName' || key === 'Email') {
        return;
      }

      const field = formData[key];
      if (field && typeof field === 'object' && field.value !== undefined) {
        resourceRequest[key] = field.value;
      } else {
        resourceRequest[key] = field;
      }
    });

    return resourceRequest;
  };

  const handleSaveTemplate = async () => {
    // Validation
    if (!templateName.trim()) {
      setTemplateError('Template name cannot be blank');
      return;
    }

    setLoading(true);
    setTemplateError('');

    try {
      const requesterID = sessionStorage.getItem("userId");
      const OrganizationId = sessionStorage.getItem("organizationId");
      const activeAccountId = sessionStorage.getItem("activeAccountId") ; // Default or get from context
      const region = formData?.location?.value || "us-east-1"; // Get region from form data
      const environment = formData?.environment?.value || "Development"; // Get environment from form data

      // Transform formData to resourceRequest format (excluding applicationId and ApplicationName)
      const resourceRequest = transformFormDataToResourceRequest(formData);

      additionalTags.forEach((tag) => {
        if (tag.key && tag.value) {
          resourceRequest[tag.key] = `${tag.value}|additionalTagsTrue`;
        }
      });
      // const cloudProviderMap = {
      //   'AWS': '2',
      //   'Azure': '1',
      //   'GCP': '3'
      // };

      const normalizedProvider = selectedProvider?.toLowerCase();

      const cloudProviderMap = {
        aws: 2,
        azure: 1,
        gcp: 3
      };

      const mappedCloudProvider = cloudProviderMap[normalizedProvider];

      if (mappedCloudProvider == null) {
        setTemplateError("Invalid cloud provider selected.");
        setLoading(false);
        return;
      }

      const templateData = {
        templateName: templateName.trim(),
        activeAccountId: activeAccountId,
        resourceRequest: JSON.stringify(resourceRequest),
        CreatedBy: requesterID,
        region: region,
        resourceType: resourceId,
        OrganizationId: OrganizationId,
        cloudProvider: mappedCloudProvider,
        Environment: environment
      };



      // const templateData = {
      //   templateName: templateName.trim(),
      //   activeAccountId: activeAccountId,
      //   resourceRequest: JSON.stringify(resourceRequest),
      //   CreatedBy: requesterID,
      //   region: region,
      //   resourceType: resourceId,
      //   OrganizationId: OrganizationId,
      //   cloudProvider: cloudProviderMap[selectedProvider] || selectedProvider,
      //   Environment: environment
      // };
      // Use the correct endpoint
      const response = await api.post('/api/resources/createTemplate', templateData, {
        headers: {
          "X-CORRELATION-ID": "569f5f1f-732a-40d7-b47f-0b45afe023b2",
          accept: "application/json",
          "Content-Type": "application/json"
        }, skipAutoSafeToast: true
      });
      if (response.status === 200) {
        toast.success("Template saved successfully!");
        handleClose();
      }
    } catch (error) {
      console.error("Error saving template:", error);

      const status = error.response?.status;
      const message = error.response?.data?.message;

      if (status === 400) {
        setTemplateError(
          message || 'Template name already exists. Please choose a different name.'
        );
      } else {
        setTemplateError(
          message || 'An error occurred while saving the template.'
        );
      }
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    onClose();
    setTemplateName('');
    setTemplateError('');
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      slotProps={{
        paper: {
          component: "form",
          noValidate: true,
          onSubmit: (e) => {
            e.preventDefault();
            e.stopPropagation();
            handleSaveTemplate();
          }
        }
      }}
    >
      <DialogTitle sx={{ fontSize: '18px', fontWeight: 'Bold' }}>
        <Typography>Save as Template</Typography>
      </DialogTitle>

      <DialogContent>
        <TextField
          fullWidth
          label="Template Name"
          value={templateName}
          onChange={(e) => {
            setTemplateName(e.target.value);
            setTemplateError('');
          }}
          margin="normal"
          required
          error={Boolean(templateError)}
          helperText={templateError || " "}
          autoFocus
        />
        <Box sx={{ mt: 2 }}>
          <Typography variant="body2" color="textSecondary">
            Resource: {resourceName}
          </Typography>
          <Typography variant="body2" color="textSecondary">
            Provider: {selectedProvider}
          </Typography>
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 3, pt: 0 }}>
        <Button onClick={handleClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          type="submit"
          variant="contained"
          disabled={loading || !templateName.trim()}
        >
          {loading ? <CircularProgress size={20} /> : 'Save Template'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

SaveTemplateDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  formData: PropTypes.object.isRequired,
  additionalTags: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string,
      value: PropTypes.string,
    })
  ).isRequired,
  resourceId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  resourceName: PropTypes.string.isRequired,
  selectedProvider: PropTypes.string.isRequired,
};

export default SaveTemplateDialog;
